import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeContext } from '@/contexts/ThemeContext'
import '@/styles/theme-switcher.css'

export type ThemeMode = 'light' | 'dark' | 'auto'

export function ThemeSwitcher() {
  const { t } = useTranslation()
  const { themeMode, setThemeMode } = useThemeContext()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const themes = [
    { mode: 'light' as ThemeMode, name: t('theme.light'), icon: 'fa-sun' },
    { mode: 'dark' as ThemeMode, name: t('theme.dark'), icon: 'fa-moon' },
    { mode: 'auto' as ThemeMode, name: t('theme.auto'), icon: 'fa-desktop' },
  ]

  const handleThemeChange = (mode: ThemeMode) => {
    setThemeMode(mode)
    setIsOpen(false)
  }

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const currentTheme = themes.find(theme => theme.mode === themeMode) || themes[0]

  return (
    <div className="theme-switcher" ref={dropdownRef}>
      <button
        className="theme-switcher-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('theme.switchTheme')}
      >
        <i className={`fas ${currentTheme.icon}`}></i>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </button>
      {isOpen && (
        <div className="theme-dropdown">
          {themes.map((theme) => (
            <button
              key={theme.mode}
              className={`theme-option ${themeMode === theme.mode ? 'active' : ''}`}
              onClick={() => handleThemeChange(theme.mode)}
            >
              <i className={`fas ${theme.icon}`}></i>
              <span className="theme-name">{theme.name}</span>
              {themeMode === theme.mode && (
                <i className="fas fa-check"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
